import { type ReactNode, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/store";
import { useTask, useUpdateTask, useDeleteTask } from "@/hooks/use-tasks";
import {
  Breadcrumbs,
  Button,
  Card,
  PageSkeleton,
  inputClass,
  InlineEditable,
  InlineSelectEditor,
  InlineDateEditor,
  InlineTextEditor,
  InlineTextareaEditor,
} from "@/components/ui";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { TaskStatusBadge } from "@/components/tasks/task-status-badge";
import { PriorityIndicator } from "@/components/tasks/priority-indicator";
import { AssigneeCell } from "@/components/tasks/assignee-cell";
import { AssigneeCombobox, type AssigneeValue } from "@/components/tasks/assignee-combobox";
import { LinkedEntityLink } from "@/components/tasks/linked-entity-link";
import { linkedRecordLabel } from "@/components/violations/linked-record-label";
import { Avatar } from "@/components/tasks/avatar";
import { SubtaskList } from "@/components/tasks/subtask-list";
import { TaskCommentThread } from "@/components/tasks/task-comment-thread";
import { TaskAttachmentGallery } from "@/components/tasks/task-attachment-gallery";
import { RecentPagesChip } from "@/components/recent-pages/recent-pages-chip";
import { useToast } from "@/hooks/use-toast";
import { useInlineField } from "@/hooks/use-inline-field";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { formatDateOnly, formatDateTime } from "@/lib/dates";
import { type components } from "@njbsoft/types";

export const Route = createFileRoute("/_authenticated/tasks/$id/")({
  component: TaskDetailPage,
});

type Task = components["schemas"]["Task"];
type TaskPriority = components["schemas"]["TaskPriority"];
type TaskStatus = components["schemas"]["TaskStatus"];
type TaskUpdate = components["schemas"]["TaskUpdate"];

/** The four priority values, in display order, for the sidebar's inline select. */
const PRIORITY_VALUES: TaskPriority[] = ["low", "normal", "high", "urgent"];

/** The four status values, in display order, for the sidebar's FSM-filtered select. */
const STATUS_ORDER: TaskStatus[] = ["open", "in_progress", "completed", "cancelled"];

/**
 * Maps a task's `linked_entity_type` to the matching FK column value so the
 * LinkedEntityLink can build the entity detail route. At most one of the six FK
 * columns is set per task (DB CHECK tasks_entity_link_one), and it always
 * agrees with `linked_entity_type`.
 */
function linkedEntityId(task: Task): string | undefined {
  switch (task.linked_entity_type) {
    case "facility":
      return task.facility_id ?? undefined;
    case "device_installation":
      return task.device_installation_id ?? undefined;
    case "account":
      return task.account_id ?? undefined;
    case "violation":
      return task.violation_id ?? undefined;
    default:
      return undefined;
  }
}

/**
 * Derives the AssigneeCombobox value from a task's assignee FK columns for
 * the sidebar's inline editor (person takes priority, matching the DB CHECK
 * `tasks_assignee_xor` — at most one of the two is ever set).
 */
function assigneeValueFromTask(task: Task): AssigneeValue {
  if (task.assigned_to_profile_id) {
    return { kind: "person", profileId: task.assigned_to_profile_id };
  }
  if (task.assigned_to_group_id) {
    return { kind: "group", groupId: task.assigned_to_group_id };
  }
  return { kind: "unassigned" };
}

/**
 * Derives the i18n key for a stored RRULE's cadence. The form persists bare
 * RRULE values (e.g. "FREQ=MONTHLY"); a non-standard FREQ falls back to the
 * "custom" label. Returns a `form.recurrence.*` key the sidebar renders.
 */
function recurrenceCadenceKey(rule: string): string {
  const normalized = rule.startsWith("RRULE:") ? rule.slice(6) : rule;
  const freqMatch = /FREQ=([A-Z]+)/.exec(normalized);
  switch (freqMatch?.[1]) {
    case "DAILY":
      return "form.recurrence.daily";
    case "WEEKLY":
      return "form.recurrence.weekly";
    case "MONTHLY":
      return "form.recurrence.monthly";
    default:
      return "form.recurrence.custom";
  }
}

function TaskDetailPage() {
  const { t } = useTranslation(["tasks", "common", "violations"]);
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const orgId = useAppStore((s) => s.activeOrgId);
  const { data: task, isLoading, isError } = useTask(id);
  const updateTask = useUpdateTask(id);
  const deleteTask = useDeleteTask(id);
  const toast = useToast();
  useRecentPageTitle(task?.title);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  /** Centralizes an inline-field PATCH + success/error toast for the Details sidebar. */
  const patchField = useInlineField<TaskUpdate>((body) => updateTask.mutateAsync(body), {
    successKey: "tasks:toasts.updated",
  });

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("common:error")}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !task) {
    return (
      <div className="p-8">
        <div className="rounded-md bg-danger/10 p-4 text-sm text-danger">
          {t("errors.notFound")}
        </div>
      </div>
    );
  }

  // ── FSM-gated status transitions ─────────────────────────────────────────
  // FSM: open→{in_progress, completed, cancelled}
  //      in_progress→{open, completed, cancelled}
  //      completed→{open}          (reopen only)
  //      cancelled→{}              (terminal — no outbound edges)
  const status = task.status;
  const canStart = status === "open";
  const canReopen = status === "in_progress" || status === "completed";
  const canComplete = status === "open" || status === "in_progress";
  const canCancel = status === "open" || status === "in_progress";

  const handleStatusChange = async (nextStatus: "open" | "in_progress" | "completed") => {
    try {
      await updateTask.mutateAsync({ status: nextStatus });
      toast.success("tasks:toasts.updated");
    } catch (err) {
      const code = err instanceof Error ? (err as { code?: string }).code : undefined;
      toast.apiError(code);
    }
  };

  // Priority and due date stay editable through both live FSM states;
  // completed/cancelled tasks are historical records.
  const fieldsEditable = status === "open" || status === "in_progress";

  const priorityOptions = PRIORITY_VALUES.map((v) => ({ value: v, label: t(`priority.${v}`) }));

  // FSM-valid next statuses for the current status, plus the current status
  // itself (so re-selecting it is a no-op). Reuses the same
  // canStart/canReopen/canComplete/canCancel gates the quick-transition
  // buttons above are built from.
  const statusOptions = STATUS_ORDER.filter(
    (s) =>
      s === status ||
      (s === "in_progress" && canStart) ||
      (s === "open" && canReopen) ||
      (s === "completed" && canComplete) ||
      (s === "cancelled" && canCancel),
  ).map((s) => ({ value: s, label: t(`status.${s}`) }));

  const handleDelete = async () => {
    try {
      await deleteTask.mutateAsync();
      void navigate({ to: "/tasks" });
    } catch (err) {
      setConfirmDelete(false);
      const code = err instanceof Error ? (err as { code?: string }).code : undefined;
      toast.apiError(code);
    }
  };

  const handleCancel = async () => {
    try {
      await updateTask.mutateAsync({ status: "cancelled" });
      setConfirmCancel(false);
      toast.success("tasks:toasts.updated");
    } catch (err) {
      setConfirmCancel(false);
      const code = err instanceof Error ? (err as { code?: string }).code : undefined;
      toast.apiError(code);
    }
  };

  // ── Derived display values ───────────────────────────────────────────────
  const isOverdue =
    task.due_date !== null &&
    task.due_date !== undefined &&
    new Date(task.due_date) < new Date() &&
    status !== "completed" &&
    status !== "cancelled";

  const subtasks = task.subtasks ?? [];
  const subtasksDone = subtasks.filter((s) => s.status === "completed").length;
  const subtaskPct = subtasks.length > 0 ? Math.round((subtasksDone / subtasks.length) * 100) : 0;

  return (
    <div className="p-8">
      <Breadcrumbs items={[{ label: t("list.title"), to: "/tasks" }, { label: task.title }]} />

      {/* ── Title + primary action row ─────────────────────────────────────── */}
      <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-2xl font-bold text-on-surface">
          <InlineEditable
            label={t("detail.renameTask")}
            disabled={!fieldsEditable}
            display={<span>{task.title}</span>}
          >
            {(close) => (
              <InlineTextEditor
                ariaLabel={t("detail.renameTask")}
                initial={task.title}
                close={close}
                onSubmit={(v) => patchField({ title: v })}
              />
            )}
          </InlineEditable>
        </h1>
        <div className="flex flex-wrap gap-2">
          <RecentPagesChip />
          {canComplete && (
            <Button
              size="sm"
              onClick={() => void handleStatusChange("completed")}
              disabled={updateTask.isPending}
            >
              {"✓ "}
              {t("form.complete")}
            </Button>
          )}
          <Button
            size="sm"
            variant="danger"
            onClick={() => setConfirmDelete(true)}
            disabled={deleteTask.isPending}
          >
            {t("form.delete")}
          </Button>
        </div>
      </div>

      {/* ── Recurring / spawned-from banners ──────────────────────────────── */}
      {task.recurrence_rule && (
        <div className="mt-4 rounded-lg bg-primary/5 px-4 py-2.5 text-sm text-on-surface">
          {"⟳ "}
          {t("detail.recurringNote")}
        </div>
      )}
      {task.recurrence_prev_id && !task.recurrence_rule && (
        <div className="mt-4 rounded-lg bg-primary/5 px-4 py-2.5 text-sm text-on-surface">
          {t("detail.spawnedFrom")}
        </div>
      )}

      {/* ── Two-column layout: main (left) + Details sidebar (right) ───────── */}
      <div className="mt-4 grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_300px]">
        {/* ── Main column ─────────────────────────────────────────────────── */}
        <div className="space-y-4">
          {/* Status row + Description */}
          <Card>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <TaskStatusBadge status={task.status} label={t(`status.${task.status}`)} />
              {canStart && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void handleStatusChange("in_progress")}
                  disabled={updateTask.isPending}
                >
                  {t("form.markInProgress")}
                </Button>
              )}
              {canReopen && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void handleStatusChange("open")}
                  disabled={updateTask.isPending}
                >
                  {t("form.reopen")}
                </Button>
              )}
              {canCancel && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setConfirmCancel(true)}
                  disabled={updateTask.isPending}
                >
                  {t("form.markCancelled")}
                </Button>
              )}
            </div>

            <SectionHeading>{t("detail.description")}</SectionHeading>
            <div>
              <InlineEditable
                label={t("detail.editDescription")}
                disabled={!fieldsEditable}
                display={
                  task.description ? (
                    <span className="whitespace-pre-wrap text-sm text-on-surface">
                      {task.description}
                    </span>
                  ) : (
                    <span className="text-sm text-muted">{t("detail.noDescription")}</span>
                  )
                }
              >
                {(close) => (
                  <InlineTextareaEditor
                    ariaLabel={t("detail.editDescription")}
                    initial={task.description ?? ""}
                    placeholder={t("form.fields.descriptionPlaceholder")}
                    close={close}
                    onSubmit={(v) => patchField({ description: v || null })}
                  />
                )}
              </InlineEditable>
            </div>
          </Card>

          {/* Subtasks */}
          <Card>
            <SectionHeading>
              {t("detail.subtasksProgress", { done: subtasksDone, total: subtasks.length })}
            </SectionHeading>
            {subtasks.length > 0 && (
              <div
                data-testid="subtask-progress-bar"
                className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"
              >
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all"
                  style={{ width: `${subtaskPct}%` }}
                />
              </div>
            )}
            <SubtaskList parentId={task.id} subtasks={subtasks} />
          </Card>

          {/* Attachments */}
          <Card>
            <SectionHeading>{t("attachments.title")}</SectionHeading>
            <TaskAttachmentGallery taskId={task.id} />
          </Card>

          {/* Comments */}
          <Card>
            <SectionHeading>{t("comments.title")}</SectionHeading>
            <TaskCommentThread taskId={task.id} />
          </Card>
        </div>

        {/* ── Details sidebar ─────────────────────────────────────────────── */}
        <Card>
          <div className="flex items-center justify-between gap-2">
            <SectionHeading>{t("detail.detailsTitle")}</SectionHeading>
            {fieldsEditable && (
              <Button
                size="sm"
                variant="ghost"
                className="-my-1"
                onClick={() => void navigate({ to: "/tasks/$id/edit", params: { id } })}
              >
                {t("detail.editDetails")}
              </Button>
            )}
          </div>
          <dl className="space-y-0">
            {/* Status */}
            <MetaRow label={t("columns.status")}>
              <InlineEditable
                label={t("columns.status")}
                disabled={status === "cancelled"}
                display={
                  <TaskStatusBadge status={task.status} label={t(`status.${task.status}`)} />
                }
              >
                {(close) => (
                  <InlineSelectEditor
                    ariaLabel={t("columns.status")}
                    initial={status}
                    options={statusOptions}
                    close={close}
                    onSubmit={(v) =>
                      // v comes from statusOptions (TaskStatus[]) — narrowing the editor's
                      // string callback is safe
                      patchField({ status: v as TaskStatus })
                    }
                  />
                )}
              </InlineEditable>
            </MetaRow>

            {/* Assignee */}
            <MetaRow label={t("columns.assignee")}>
              <InlineEditable
                label={t("columns.assignee")}
                disabled={!fieldsEditable}
                display={<AssigneeSidebarValue task={task} />}
              >
                {(close) => (
                  <AssigneeCombobox
                    mode="all"
                    value={assigneeValueFromTask(task)}
                    onChange={(v) => {
                      const nextProfileId = v.kind === "person" ? (v.profileId ?? null) : null;
                      const nextGroupId = v.kind === "group" ? (v.groupId ?? null) : null;
                      // Skip a redundant PATCH + false "updated" toast when the
                      // selection matches the current assignee (mirrors the
                      // subtask row's handleAssigneeChange guard).
                      if (
                        nextProfileId === (task.assigned_to_profile_id ?? null) &&
                        nextGroupId === (task.assigned_to_group_id ?? null)
                      ) {
                        close();
                        return;
                      }
                      void patchField({
                        assigned_to_profile_id: nextProfileId,
                        assigned_to_group_id: nextGroupId,
                      });
                      close();
                    }}
                  />
                )}
              </InlineEditable>
            </MetaRow>

            {/* Priority */}
            <MetaRow label={t("columns.priority")}>
              <InlineEditable
                label={t("columns.priority")}
                disabled={!fieldsEditable}
                display={
                  <PriorityIndicator
                    priority={task.priority}
                    label={t(`priority.${task.priority}`)}
                  />
                }
              >
                {(close) => (
                  <InlineSelectEditor
                    ariaLabel={t("columns.priority")}
                    initial={task.priority}
                    options={priorityOptions}
                    close={close}
                    onSubmit={(v) =>
                      // v comes from priorityOptions (TaskPriority[]) — narrowing the editor's
                      // string callback is safe
                      patchField({ priority: v as TaskPriority })
                    }
                  />
                )}
              </InlineEditable>
            </MetaRow>

            {/* Due */}
            <MetaRow label={t("columns.dueDate")}>
              <InlineEditable
                label={t("columns.dueDate")}
                disabled={!fieldsEditable}
                display={
                  task.due_date ? (
                    <span
                      className={`text-xs ${isOverdue ? "font-semibold text-danger" : "text-on-surface"}`}
                    >
                      {formatDateOnly(task.due_date)}
                      {isOverdue && <span className="ml-1">· {t("list.overdue")}</span>}
                    </span>
                  ) : (
                    <span className="text-xs text-muted">{"—"}</span>
                  )
                }
              >
                {(close) => (
                  <InlineDateEditor
                    ariaLabel={t("columns.dueDate")}
                    initial={task.due_date ?? ""}
                    close={close}
                    onSubmit={(v) => patchField({ due_date: v || null })}
                  />
                )}
              </InlineEditable>
            </MetaRow>

            {/* Labels */}
            <MetaRow label={t("columns.labels")}>
              <InlineEditable
                label={t("columns.labels")}
                disabled={!fieldsEditable}
                display={
                  task.labels.length > 0 ? (
                    <span className="flex flex-wrap gap-1.5">
                      {task.labels.map((l) => (
                        <span
                          key={l}
                          className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[10.5px] font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                        >
                          {l}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span className="text-xs text-muted">
                      {fieldsEditable ? t("detail.addLabels") : "—"}
                    </span>
                  )
                }
              >
                {(close) => (
                  <LabelsInlineEditor
                    initial={task.labels}
                    onCommit={(next) => patchField({ labels: next })}
                    close={close}
                  />
                )}
              </InlineEditable>
            </MetaRow>

            {/* Linked entity */}
            {task.linked_entity_type && (
              <MetaRow label={t("detail.linkedTo")}>
                <LinkedEntityLink
                  type={task.linked_entity_type}
                  label={linkedRecordLabel(t, task.linked_entity_type, task.linked_entity_label)}
                  id={linkedEntityId(task)}
                />
              </MetaRow>
            )}

            {/* Repeats */}
            {task.recurrence_rule && (
              <MetaRow label={t("detail.repeats")}>
                <span className="text-xs text-on-surface">
                  {t(recurrenceCadenceKey(task.recurrence_rule))}
                </span>
              </MetaRow>
            )}

            {/* Created */}
            <MetaRow label={t("columns.createdBy")}>
              {task.created_by_name ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-on-surface">
                  <Avatar seed={task.created_by_name} label={task.created_by_name} size="sm" />
                  {t("detail.createdOn", {
                    name: task.created_by_name,
                    date: formatDateOnly(task.created_at.slice(0, 10)),
                  })}
                </span>
              ) : (
                <span className="text-xs text-on-surface">
                  {formatDateOnly(task.created_at.slice(0, 10))}
                </span>
              )}
            </MetaRow>

            {/* Completed (with completer name when available) */}
            {task.completed_at && (
              <MetaRow label={t("status.completed")}>
                <span className="text-xs text-on-surface">
                  {task.completed_by_name
                    ? t("detail.completedAt", {
                        date: formatDateTime(task.completed_at),
                        name: task.completed_by_name,
                      })
                    : formatDateTime(task.completed_at)}
                </span>
              </MetaRow>
            )}

            {/* Updated */}
            {task.updated_at && (
              <MetaRow label={t("columns.updatedAt")}>
                <span className="text-xs text-on-surface">{formatDateTime(task.updated_at)}</span>
              </MetaRow>
            )}
          </dl>
        </Card>
      </div>

      {/* ── Confirm: delete task ───────────────────────────────────────────── */}
      <ConfirmDialog
        open={confirmDelete}
        title={t("confirm.deleteTask.title")}
        message={t("confirm.deleteTask.body", { title: task.title })}
        confirmLabel={t("confirm.deleteTask.confirm")}
        cancelLabel={t("confirm.deleteTask.cancel")}
        variant="danger"
        confirmDisabled={deleteTask.isPending}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
      />

      {/* ── Confirm: cancel task (status transition) ───────────────────────── */}
      <ConfirmDialog
        open={confirmCancel}
        title={t("confirm.cancelTask.title")}
        message={t("confirm.cancelTask.body", { title: task.title })}
        confirmLabel={t("confirm.cancelTask.confirm")}
        cancelLabel={t("confirm.cancelTask.cancel")}
        variant="danger"
        confirmDisabled={updateTask.isPending}
        onConfirm={() => void handleCancel()}
        onCancel={() => setConfirmCancel(false)}
      />
    </div>
  );
}

// ── Layout primitives ────────────────────────────────────────────────────────

/** Uppercase muted section label, matching the mockup `.sec-h` style. */
function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2.5 text-[11px] font-bold uppercase tracking-wider text-muted">
      {children}
    </div>
  );
}

/** A single metadata row in the Details sidebar (fixed-width label + value). */
function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 border-b border-gray-100 py-2.5 last:border-b-0 dark:border-gray-700">
      <dt className="w-20 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-muted">
        {label}
      </dt>
      <dd className="flex flex-1 flex-wrap items-center gap-1.5 text-sm text-on-surface">
        {children}
      </dd>
    </div>
  );
}

/**
 * Renders the sidebar assignee value: a person Avatar+name when
 * profile-assigned, a 👥 group tag when group-assigned (group name resolved
 * server-side and carried on the task as assigned_to_group_name), or "—" when
 * unassigned.
 */
function AssigneeSidebarValue({ task }: { task: Task }) {
  const { t } = useTranslation("tasks");

  if (task.assigned_to_profile_id) {
    return <AssigneeCell name={task.assigned_to_name || t("form.assignee.person")} />;
  }
  if (task.assigned_to_group_id) {
    return <AssigneeCell groupName={task.assigned_to_group_name || t("form.assignee.group")} />;
  }
  return <AssigneeCell />;
}

/**
 * Inline label-chip editor for the Details sidebar. Mirrors the chip-input
 * pattern in task-form.tsx (Enter-to-add text field + secondary Add button +
 * removable "✕" chips) but commits the FULL label array immediately on every
 * add/remove — the optimistic single-field PATCH posture the rest of the
 * sidebar's inline editors already follow, rather than batching until close.
 */
function LabelsInlineEditor({
  initial,
  onCommit,
  close,
}: {
  initial: string[];
  onCommit: (next: string[]) => void | Promise<void>;
  close: () => void;
}) {
  const { t } = useTranslation(["tasks", "common"]);
  const [labels, setLabels] = useState<string[]>(initial);
  const [labelInput, setLabelInput] = useState("");

  function addLabel() {
    const trimmed = labelInput.trim();
    if (trimmed && !labels.includes(trimmed)) {
      const next = [...labels, trimmed];
      setLabels(next);
      void onCommit(next);
    }
    setLabelInput("");
  }

  function removeLabel(label: string) {
    const next = labels.filter((l) => l !== label);
    setLabels(next);
    void onCommit(next);
  }

  return (
    <div className="w-full min-w-[220px] space-y-1.5">
      <div className="flex gap-1.5">
        <input
          autoFocus
          aria-label={t("form.fields.labels")}
          className={inputClass}
          type="text"
          value={labelInput}
          placeholder={t("form.fields.labelsPlaceholder")}
          onChange={(e) => setLabelInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addLabel();
            } else if (e.key === "Escape") {
              e.preventDefault();
              close();
            }
          }}
        />
        <Button type="button" size="sm" variant="secondary" onClick={addLabel}>
          {t("common:add")}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={close}>
          {t("common:close")}
        </Button>
      </div>
      {labels.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {labels.map((label) => (
            <span
              key={label}
              className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-[10.5px] font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200"
            >
              {label}
              <button
                type="button"
                className="text-gray-400 hover:text-danger"
                onClick={() => removeLabel(label)}
                aria-label={t("common:remove")}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
